/** A rule in words, for the list and the delete dialog. Pure; ids resolve through `Names`.
 *
 *  The wording is the design's (`condText` / `actText` in the Auto-rules
 *  artboard): a text value is quoted, a list reads "is one of A, B, C", and an
 *  action is what changes — "Category → Groceries", "+ #weekly-shop". */
import { formatCents } from '../../api/parseMoney'
import {
  ANY_AMOUNT, FIELDS, amountDirection, isListOp, typedCents, type ActionDraft, type ConditionDraft, type Direction,
  type RuleDraft,
} from './ruleWire'

/** A sentence in pieces: words, and amounts as cents. Only `Money` renders
 *  currency (rule 4), so the pieces are drawn by `RuleSentence`; the plain-text
 *  form below carries the bare decimal, for the list's filter. */
export type Part = string | { cents: number }

/** A merchant id as a sentence can name it. A merchant is looked up by id (the user may have more than
 *  any list page holds), so the lookup has states: still loading, found, gone (the server says no such
 *  merchant, or answers with a DELETED one, `is_active: false`, which the rule engine skips), or
 *  unavailable (the lookup failed, which says nothing about the merchant). */
export type MerchantName =
  | { status: 'loading' }
  | { status: 'found'; name: string }
  | { status: 'unknown' }
  | { status: 'unavailable' }

export type Names = {
  /** A category's name; `undefined` for one that does not exist, or while they load. */
  category: (id: string) => string | undefined
  /** The categories are still loading, so an unresolved id is not yet "Unknown". */
  categoriesLoading: boolean
  account: (id: string) => string | undefined
  merchant: (id: string) => MerchantName
}

/** A merchant id in words: its name; "Unknown merchant" once the server says it names none; otherwise
 *  "a merchant", which claims nothing while it loads or when the lookup failed. Never the raw id. */
export function merchantWords(m: MerchantName): string {
  if (m.status === 'found') return m.name
  return m.status === 'unknown' ? 'Unknown merchant' : 'a merchant'
}

/** ISO weekday digits, as the API stores them. */
export const DOW_LABEL: Record<string, string> = {
  '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The operator as the list says it. `is_in` and `between` are phrased by their callers. */
const TEXT_OP = {
  equals: 'equals',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_in: 'is one of',
  before: 'before',
  after: 'after',
  between: 'between',
  greater_than: 'is over',
  less_than: 'is under',
  greater_or_equal: 'is at least',
  not_equals: 'doesn’t equal',
  not_contains: 'doesn’t contain',
  not_in: 'isn’t any of',
} as const

/** "Jan 2" or "Jan 2, 2026" from an ISO date. Split, never `new Date`: a
 *  parsed date moves a day in any zone behind UTC. Unreadable input is shown
 *  as it was given. */
function day(iso: string, year: boolean): string {
  const [y, m, d] = iso.split('-')
  const month = MONTHS[Number(m) - 1]
  const n = Number(d)
  if (y === undefined || month === undefined || !Number.isInteger(n) || n < 1) return iso
  return `${month} ${n}${year ? `, ${y}` : ''}`
}

function display(c: ConditionDraft, raw: string, names: Names): string {
  if (c.field === 'day_of_week') return DOW_LABEL[raw] ?? raw
  if (c.field === 'account_id') return names.account(raw) ?? '(deleted account)'
  return raw
}

/** The subject of an amount sentence, by direction. */
const AMOUNT_SUBJECT: Record<Direction, string> = {
  spent: 'Amount spent',
  received: 'Amount received',
  any: 'Amount in or out',
}
const ANY_AMOUNT_TEXT: Record<Direction, string> = {
  spent: 'Money out, any amount',
  received: 'Money in, any amount',
  any: 'Any amount, in or out',
}
const AMOUNT_OP: Partial<Record<ConditionDraft['op'], string>> = {
  equals: 'is exactly',
  greater_than: 'is over',
  less_than: 'is under',
  between: 'is between',
}

/** A stored or typed amount as a cents part; text that does not parse (an
 *  unfinished draft) is shown as it was typed rather than dropped. */
const amountPart = (v: string): Part => {
  const cents = typedCents(v)
  return cents === null ? v.trim() : { cents }
}

function amountParts(c: ConditionDraft): Part[] {
  const direction = amountDirection(c)
  if (c.op === ANY_AMOUNT) return [ANY_AMOUNT_TEXT[direction]]
  const head = `${AMOUNT_SUBJECT[direction]} ${AMOUNT_OP[c.op] ?? TEXT_OP[c.op]} `
  if (c.op === 'between') return [head, amountPart(c.value), ' and ', amountPart(c.value2)]
  return [head, amountPart(c.value)]
}

export function conditionParts(c: ConditionDraft, names: Names): Part[] {
  return c.field === 'amount' ? amountParts(c) : [conditionWords(c, names)]
}

/** Every condition but an amount, which is all words. */
function conditionWords(c: ConditionDraft, names: Names): string {
  const { label, kind } = FIELDS[c.field]
  if (kind === 'date') {
    if (c.op === 'between') return `${label} between ${day(c.value, false)} and ${day(c.value2, true)}`
    return `${label} ${TEXT_OP[c.op]} ${day(c.value, true)}`
  }
  const not = c.op === 'not_equals' || c.op === 'not_in'
  const shown = (isListOp(c.op) ? c.list : [c.value]).map((v) => display(c, v, names))
  if (kind === 'dow') return `Day ${not ? 'isn’t' : 'is'} ${shown.join(' or ')}`
  // A select names things, so "isn't A or B"; text values read as a list.
  if (kind === 'select' && not) return `${label} isn’t ${shown.join(' or ')}`
  if (isListOp(c.op)) return `${label} ${TEXT_OP[c.op]} ${shown.join(', ')}`
  if (kind === 'select') return `${label} is ${shown.join('')}`
  return `${label} ${TEXT_OP[c.op]} “${c.value.trim()}”`
}

/** A part as plain text: an amount is its bare decimal, never a currency string. */
const plain = (p: Part): string => (typeof p === 'string' ? p : formatCents(p.cents))

export function conditionText(c: ConditionDraft, names: Names): string {
  return conditionParts(c, names).map(plain).join('')
}

/** The when-sentence in pieces: conditions joined by their group's word, groups by "or". */
export function whenParts(d: RuleDraft, names: Names): Part[] {
  return d.groups.flatMap((g, gi) => [
    ...(gi > 0 ? [' or '] : []),
    ...g.conditions.flatMap((c, ci) => [...(ci > 0 ? [` ${g.join} `] : []), ...conditionParts(c, names)]),
  ])
}

export function whenText(d: RuleDraft, names: Names): string {
  return whenParts(d, names).map(plain).join('')
}

/** A tag with exactly one leading hash, whether or not it was stored with one. */
const hashed = (tag: string) => `#${tag.trim().replace(/^#+/, '')}`

export function actionText(a: ActionDraft, names: Names): string {
  const v = a.value.trim()
  switch (a.type) {
    case 'set_category':
      return `Category → ${names.category(a.value) ?? '(deleted category)'}`
    case 'add_tag':
      return `+ ${hashed(v)}`
    case 'remove_tag':
      return `− ${hashed(v)}`
    case 'set_merchant':
      return `Merchant → ${merchantWords(names.merchant(v))}`
    case 'set_note':
      return `Note → “${v}”`
    case 'append_note':
      return `Note + “${v}”`
    case 'flag_for_review':
      return 'Flag for review'
    case 'exclude_from_budget':
      return 'Not in budget'
    case 'exclude_from_reports':
      return 'Not in reports'
  }
}
